import type { operations } from "@airange/generated-contracts/edu";
import { eduAdminOpenApiClient } from "../generated/client";
import { omitEmptyQuery } from "./openapi-query";
import { unwrapOpenApiResult } from "./openapi-result";

type OperationResponse<
  TOperation extends { responses: Record<number, unknown> },
  TStatus extends keyof TOperation["responses"],
> = TOperation["responses"][TStatus] extends {
  content: { "application/json": infer TResponse };
}
  ? TResponse
  : never;

type OperationRequestBody<TOperation extends { requestBody?: unknown }> = NonNullable<
  TOperation["requestBody"]
> extends {
  content: { "application/json": infer TBody };
}
  ? TBody
  : never;

type OperationQuery<TOperation extends { parameters: { query?: unknown } }> = NonNullable<
  TOperation["parameters"]["query"]
>;

type OperationPath<TOperation extends { parameters: { path?: unknown } }> = NonNullable<
  TOperation["parameters"]["path"]
>;

type UiNonNullQuery<TQuery extends object, TReplaced extends keyof TQuery = never> = {
  [TKey in Exclude<keyof TQuery, TReplaced>]?: NonNullable<TQuery[TKey]>;
};

type GetCoursesOverviewOperation = operations["get_courses_overview_api_courses_overview_get"];
type GetCoursesOperation = operations["get_courses_api_courses_get"];
type CreateCourseOperation = operations["post_course_api_courses_post"];
type GetCourseOperation = operations["get_course_api_courses__course_id__get"];
type UpdateCourseSchemeCatalogOperation =
  operations["patch_course_scheme_catalog_api_courses__course_id__scheme_catalog_patch"];
type CreateCourseMaterialOperation =
  operations["post_course_material_api_courses__course_id__materials_post"];

type CoursesGeneratedQuery = OperationQuery<GetCoursesOperation>;
type CourseId = OperationPath<GetCourseOperation>["course_id"];
type CreateCourseResponse = OperationResponse<CreateCourseOperation, 201>;
type CreateCourseMaterialResponse = OperationResponse<CreateCourseMaterialOperation, 201>;
type UpdateCourseSchemeCatalogResponse = OperationResponse<UpdateCourseSchemeCatalogOperation, 200>;

export type EduCourseListQuery = UiNonNullQuery<CoursesGeneratedQuery, "page_size"> & {
  pageSize?: NonNullable<CoursesGeneratedQuery["page_size"]>;
};
export type EduCoursesOverview = OperationResponse<GetCoursesOverviewOperation, 200>;
export type EduCourseOverviewBucket = EduCoursesOverview["buckets"][number];
export type EduCoursesListResponse = OperationResponse<GetCoursesOperation, 200>;
export type EduCourseListItem = EduCoursesListResponse["items"][number];
export type EduCourseSourceType = EduCourseListItem["source_type"];
export type EduCourseLifecycleStatus = EduCourseListItem["lifecycle_status"];
export type EduCourseListScope = NonNullable<CoursesGeneratedQuery["source_type"]>;
export type EduCourseCreatePayload = OperationRequestBody<CreateCourseOperation>;
export type EduCourseDetail = OperationResponse<GetCourseOperation, 200>;
export type EduCourseOutlineSection = EduCourseDetail["sections"][number];
export type EduCourseTaskTypeSummary = EduCourseDetail["task_type_summaries"][number];
export type EduCourseMaterialItem = EduCourseDetail["materials"][number];
export type EduCourseSchemeCatalog = EduCourseDetail["scheme_catalog"];
export type EduCourseSchemeCatalogChapter = EduCourseSchemeCatalog["chapters"][number];
export type EduCourseSchemeCatalogLesson = EduCourseSchemeCatalogChapter["lessons"][number];
export type EduCourseSchemeCatalogTask = EduCourseSchemeCatalogLesson["tasks"][number];
export type EduTaskCompletionPolicy = EduCourseSchemeCatalogTask["completion_policy"];
export type EduTaskCompletionPolicyKind = EduTaskCompletionPolicy["kind"];
export type EduCourseRequiredKind = EduCourseSchemeCatalogLesson["required_kind"];
export type EduCourseLessonType = EduCourseSchemeCatalogLesson["lesson_type"];
export type EduCourseTaskType = EduCourseSchemeCatalogTask["task_type"];
export type EduCourseTaskStageKey = EduCourseSchemeCatalogTask["stage_key"];
export type EduCourseMaterialCreatePayload = OperationRequestBody<CreateCourseMaterialOperation>;
export type EduCourseSchemeCatalogUpdatePayload = OperationRequestBody<UpdateCourseSchemeCatalogOperation>;

function toCourseListQueryParams(query?: EduCourseListQuery): CoursesGeneratedQuery | undefined {
  if (!query) return undefined;
  const { pageSize, ...generatedQuery } = query;
  return omitEmptyQuery<CoursesGeneratedQuery>({
    ...generatedQuery,
    ...(pageSize === undefined ? {} : { page_size: pageSize }),
  });
}

const eduCoursesApi = {
  async getCoursesOverview(): Promise<EduCoursesOverview> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/courses/overview"));
  },
  async getCourses(query?: EduCourseListQuery): Promise<EduCoursesListResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/courses", {
        params: { query: toCourseListQueryParams(query) },
      }),
    );
  },
  async createCourse(payload: EduCourseCreatePayload): Promise<CreateCourseResponse> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.POST("/api/courses", { body: payload }));
  },
  async getCourseDetail(courseId: CourseId): Promise<EduCourseDetail> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/courses/{course_id}", {
        params: { path: { course_id: courseId } },
      }),
    );
  },
  async createCourseMaterial(
    courseId: CourseId,
    payload: EduCourseMaterialCreatePayload,
  ): Promise<CreateCourseMaterialResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/courses/{course_id}/materials", {
        params: { path: { course_id: courseId } },
        body: payload,
      }),
    );
  },
  async updateCourseSchemeCatalog(
    courseId: CourseId,
    payload: EduCourseSchemeCatalogUpdatePayload,
  ): Promise<UpdateCourseSchemeCatalogResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.PATCH("/api/courses/{course_id}/scheme-catalog", {
        params: { path: { course_id: courseId } },
        body: payload,
      }),
    );
  },
};

export default eduCoursesApi;
